/**
 * components/ValidationPanel/ValidationPanelHorsService.js
 * Composant pour gérer les commentaires des fibres Hors Service
 */

import "./ValidationPanelHorsService.css";

import logger from "../../utils/logger.js";

export class ValidationPanelHorsService {
  constructor(state, options = {}) {
    this.state = state;
    this.options = {
      onCommentUpdated: options.onCommentUpdated || null,
      ...options,
    };

    this.element = null;
    this.fibersHS = [];
    this.comments = new Map(); // fiberReference -> comment
  }

  /**
   * Initialiser le composant
   */
  async initialize() {
    try {
      this.fibersHS = this.detectHorsServiceFibers();
      return {
        totalFibers: this.fibersHS.length,
        hasData: this.fibersHS.length > 0,
      };
    } catch (error) {
      logger.error(
        {
          context: "panel-hs-initialization",
          error: error.message,
          stack: error.stack,
          fibersHSCount: this.fibersHS?.length || 0,
          hasState: !!this.state,
          pvHsDataExists: !!this.state?.getAllSources()?.pvHs?.data,
        },
        "Erreur initialisation ValidationPanelHorsService"
      );
      throw error;
    }
  }

  /**
   * Détecter les fibres Hors Service
   */
  detectHorsServiceFibers() {
    const sources = this.state.getAllSources();
    const pvHsData = sources.pvHs?.data;

    if (!pvHsData || !Array.isArray(pvHsData.fibers)) {
      return [];
    }

    return pvHsData.fibers.map((fiber) => ({
      id: `hs_${fiber.REFERENCE || fiber.reference}_${fiber.port || fiber.PORT || "noport"}`,
      reference: fiber.REFERENCE || fiber.reference,
      port: fiber.port || fiber.PORT || null,
      originalData: fiber,
      comment: "",
    }));
  }

  /**
   * Rendu principal
   */
  render() {
    if (this.fibersHS.length === 0) {
      return this.renderEmptyState();
    }

    return `
      <div class="validation-panel-hs">
        <div class="hs-header">
          <h3 class="hs-title">Fibres Hors Service</h3>
          <span class="hs-count">${this.fibersHS.length} fibre(s)</span>
        </div>
        
        <div class="hs-list">
          ${this.fibersHS.map((fiber) => this.renderFiberItem(fiber)).join("")}
        </div>
      </div>
    `;
  }

  /**
   * Rendu d'une fibre HS
   */
  renderFiberItem(fiber) {
    // Créer une clé unique avec référence + port
    const uniqueKey = fiber.port ? `${fiber.reference}-${fiber.port}` : fiber.reference;
    const comment = this.comments.get(uniqueKey) || "";
    const hasComment = comment.trim().length > 0;
    const statusClass = hasComment ? "hs-item-completed" : "hs-item-pending";

    // Construire la référence complète avec le port formaté en 2 digits
    const formattedPort = fiber.port ? fiber.port.toString().padStart(2, "0") : null;
    const fullReference = formattedPort ? `${fiber.reference}-${formattedPort}` : fiber.reference;

    return `
    <div class="hs-item ${statusClass}" data-fiber-id="${fiber.id}">
      <div class="hs-item-header">
        <span class="hs-item-icon">${hasComment ? "✅" : "📝"}</span>
        <span class="hs-item-reference">${fullReference}</span>
      </div>
      
      <div class="hs-item-content">
        <div class="hs-input-row">
          <label class="hs-label" for="comment-${fiber.id}">
            Commentaire / Localisation :
          </label>
          <input 
            type="text"
            class="hs-comment-input" 
            id="comment-${fiber.id}"
            data-fiber-reference="${fiber.reference}"
            data-fiber-port="${fiber.port}"
            placeholder="Ex: Cassure PB12 - PM23"
            value="${comment}"
          />
        </div>
      </div>
    </div>
  `;
  }

  /**
   * État vide
   */
  renderEmptyState() {
    return `
      <div class="validation-panel-hs">
        <div class="hs-header">
          <h3 class="hs-title">Fibres Hors Service</h3>
          <span class="hs-count">0 fibre</span>
        </div>
        
        <div class="hs-empty">
          <div class="hs-empty-icon">✅</div>
          <p class="hs-empty-text">Aucune fibre hors service détectée</p>
        </div>
      </div>
    `;
  }

  /**
   * Mettre à jour un commentaire
   */
  updateComment(fiberReference, fiberPort, comment) {
    // Créer une clé unique avec référence + port
    const uniqueKey = fiberPort ? `${fiberReference}-${fiberPort}` : fiberReference;

    this.comments.set(uniqueKey, comment.trim());

    // Notifier le parent si callback fourni
    if (this.options.onCommentUpdated) {
      this.options.onCommentUpdated("hs", fiberReference, comment.trim(), fiberPort);
    }

    // Mettre à jour l'affichage - chercher par référence ET port
    this.updateItemStatus(fiberReference, fiberPort);
  }

  /**
   * Mettre à jour le statut visuel d'un item
   */
  updateItemStatus(fiberReference, fiberPort) {
    const fiber = this.fibersHS.find((f) => f.reference === fiberReference && f.port == fiberPort);
    if (!fiber) return;

    const item = this.element.querySelector(`[data-fiber-id="${fiber.id}"]`);
    if (!item) return;

    const uniqueKey = fiberPort ? `${fiberReference}-${fiberPort}` : fiberReference;
    const comment = this.comments.get(uniqueKey) || "";
    const hasComment = comment.trim().length > 0;

    // Mettre à jour les classes CSS
    item.className = `hs-item ${hasComment ? "hs-item-completed" : "hs-item-pending"}`;

    // Mettre à jour l'icône
    const icon = item.querySelector(".hs-item-icon");
    if (icon) {
      icon.textContent = hasComment ? "✅" : "📝";
    }
  }

  /**
   * Récupérer tous les commentaires
   */
  getAllComments() {
    const comments = [];

    this.fibersHS.forEach((fiber) => {
      const uniqueKey = fiber.port ? `${fiber.reference}-${fiber.port}` : fiber.reference;
      const comment = this.comments.get(uniqueKey);
      if (comment && comment.trim().length > 0) {
        comments.push({
          reference: fiber.reference,
          port: fiber.port,
          comment: comment.trim(),
          type: "hs",
        });
      }
    });

    return comments;
  }

  /**
   * Statistiques
   */
  getStats() {
    const total = this.fibersHS.length;
    const completed = this.getAllComments().length;

    return {
      total,
      completed,
      pending: total - completed,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }

  /**
   * Liaison des événements
   */
  bindEvents() {
    if (!this.element) return;

    // Événements sur les textarea
    this.element.querySelectorAll(".hs-comment-input").forEach((textarea) => {
      // Sauvegarde automatique sur blur
      textarea.addEventListener("blur", (e) => {
        const fiberReference = e.target.dataset.fiberReference;
        const fiberPort = e.target.dataset.fiberPort;
        const comment = e.target.value;
        this.updateComment(fiberReference, fiberPort, comment);
      });

      // Sauvegarde sur Enter (optionnel)
      textarea.addEventListener("keypress", (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          const fiberReference = e.target.dataset.fiberReference;
          const fiberPort = e.target.dataset.fiberPort; // ← Ajouter cette ligne
          const comment = e.target.value;
          this.updateComment(fiberReference, fiberPort, comment); // ← fiberPort était manquant
          e.target.blur();
        }
      });
    });
  }

  /**
   * Monter le composant
   */
  async mount(container) {
    try {
      await this.initialize();

      this.element = document.createElement("div");
      this.element.innerHTML = this.render();

      const mountedElement = this.element.firstElementChild;

      if (typeof container === "string") {
        const targetContainer = document.querySelector(container);
        if (targetContainer) {
          targetContainer.appendChild(mountedElement);
        } else {
          throw new Error(`Container not found: ${container}`);
        }
      } else if (container && container.appendChild) {
        container.appendChild(mountedElement);
      } else {
        throw new Error("Invalid container provided");
      }

      this.element = mountedElement;
      this.bindEvents();

      return this.element;
    } catch (error) {
      logger.error(
        {
          context: "panel-hs-mounting",
          error: error.message,
          stack: error.stack,
          containerType: typeof container,
          fibersHSCount: this.fibersHS?.length || 0,
          hasElement: !!this.element,
        },
        "Erreur montage ValidationPanelHorsService"
      );
      throw error;
    }
  }

  /**
   * Démonter le composant
   */
  unmount() {
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
  }
}
